import { createContext, useContext, useState, type ReactNode } from 'react';

type Language = 'tr' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations = {
  tr: {
    // SEO Meta
    'meta.title': 'Teknolobi - IT Danışmanlığı ve Yazılım Geliştirme',
    'meta.description': 'Teknolobi ile işletmenizi dijital dönüşümle geleceğe taşıyın. Özel yazılım geliştirme, IT danışmanlığı ve bulut çözümleri ile yanınızdayız.',
    'meta.keywords': 'IT danışmanlığı, yazılım geliştirme, dijital dönüşüm, bulut çözümleri, özel yazılım, Teknolobi',
    
    // Header
    'nav.about': 'Hakkımızda',
    'nav.company': 'Şirket Profili',
    'nav.missionvision': 'Misyon ve Vizyon',
    'nav.career': 'Kariyer',
    'nav.services': 'Hizmetler',
    'nav.services.software': 'Yazılım Geliştirme',
    'nav.services.digital': 'Dijital Dönüşüm',
    'nav.services.consulting': 'IT Danışmanlığı',
    'nav.services.cloud': 'Bulut Çözümleri',
    'nav.products': 'Ürünler',
    'nav.projects': 'Projeler',
    'nav.blog': 'Blog',
    'nav.contact': 'İletişim',
    'nav.contactButton': 'Bize Ulaşın',
    
    // Hero
    'hero.title': 'Teknolojinin Lideri, Çözümün Ortağı',
    'hero.subtitle': 'İşletmenizi dijital dönüşümle geleceğe taşıyoruz. Özelleştirilmiş yazılım çözümleri ve IT danışmanlığı ile yanınızdayız.',
    'hero.cta.primary': 'Hizmetlerimizi Keşfedin',
    'hero.cta.secondary': 'Bize Ulaşın',
    
    // Services
    'services.title': 'Hizmetlerimiz',
    'services.subtitle': 'İşletmenizin ihtiyaçlarına özel teknoloji çözümleri sunuyoruz',
    'services.custom.title': 'Özel Yazılım Geliştirme',
    'services.custom.desc': 'İşletmenize özel, ölçeklenebilir ve güvenli yazılım çözümleri geliştiriyoruz.',
    'services.digital.title': 'Dijital Dönüşüm',
    'services.digital.desc': 'İş süreçlerinizi dijitalleştirerek verimliliği artırıyor, maliyetleri düşürüyoruz.',
    'services.consulting.title': 'IT Danışmanlığı',
    'services.consulting.desc': 'Teknoloji stratejinizi oluşturuyor, doğru çözümlerle yol gösteriyoruz.',
    'services.cloud.title': 'Bulut Çözümleri',
    'services.cloud.desc': 'Modern bulut altyapısı ile işletmenizi her yerden yönetin.',
    
    // Products
    'products.title': 'Ürünlerimiz',
    'products.subtitle': 'Yenilikçi teknoloji ürünlerimizle sektöre öncülük ediyoruz',
    'products.beachbot.title': 'BeachBot',
    'products.beachbot.desc': 'Plaj yönetimini otomatikleştiren akıllı robot teknolojisi. Temizlik, güvenlik ve müşteri deneyimini bir arada sunar.',
    'products.verilobi.title': 'Verilobi',
    'products.verilobi.desc': 'İş zekası ve veri analitiği platformu. Verilerinizi anlamlı içgörülere dönüştürün, bilinçli kararlar alın.',
    'products.cta': 'Detaylı İncele',
    
    // Why Us
    'why.title': 'Neden Teknolobi?',
    'why.subtitle': 'Bizi farklı kılan değerler',
    'why.experience.title': 'Yıllık Deneyim',
    'why.experience.value': '15+',
    'why.projects.title': 'Tamamlanan Proje',
    'why.projects.value': '200+',
    'why.clients.title': 'Mutlu Müşteri',
    'why.clients.value': '150+',
    'why.team.title': 'Uzman Ekip',
    'why.team.value': '50+',
    
    // Testimonials
    'testimonials.title': 'Müşterilerimiz Ne Diyor?',
    'testimonials.subtitle': 'Başarı hikayelerimiz',
    
    // CTA
    'cta.title': 'Projenizi Hayata Geçirelim',
    'cta.subtitle': 'Uzman ekibimizle tanışın, işletmeniz için en uygun çözümü birlikte belirleyelim.',
    'cta.button': 'Ücretsiz Görüşme Talep Edin',
    
    // Footer
    'footer.about.title': 'Teknolobi',
    'footer.slogan': 'Değer katan Çözümler',
    'footer.about.desc': 'IT danışmanlığı ve yazılım geliştirme alanında öncü şirket.',
    'footer.links.title': 'Hızlı Bağlantılar',
    'footer.contact.title': 'İletişim',
    'footer.office.turkey': 'Türkiye Ofisi',
    'footer.office.canada': 'Kanada Ofisi',
    'footer.address.turkey': '23 Nisan Mh. 249. Sok. NOS Plaza 15/1 Nilüfer Bursa',
    'footer.address.canada': '6 Chartwell Rd. Unit 416 M8Z 0H2 Toronto Kanada',
    'footer.phone.turkey': '+90 850 333 30 40',
    'footer.phone.canada': '+1 647 984 77 42',
    'footer.email': 'info@teknolobi.com',
    'footer.rights': '© 2025 Teknolobi. Tüm hakları saklıdır.',
    
    // Company Profile Page
    'company.title': 'Şirket Profili',
    'company.subtitle': 'Teknolobi hakkında',
    'company.intro.title': 'Teknolojinin Gücüyle Geleceği Şekillendiriyoruz',
    'company.intro.desc': 'Teknolobi, 2010 yılından bu yana IT danışmanlığı ve yazılım geliştirme alanında öncü bir şirket olarak faaliyet göstermektedir. Müşterilerimize en yenilikçi teknoloji çözümlerini sunarak, işletmelerin dijital dönüşüm yolculuğunda güvenilir bir iş ortağı olmayı hedefliyoruz.',
    'company.history.title': 'Tarihçemiz',
    'company.history.2010': '2010 - Kuruluş',
    'company.history.2010.desc': 'İstanbul\'da küçük bir ekiple yola çıktık',
    'company.history.2015': '2015 - Büyüme',
    'company.history.2015.desc': '50+ proje tamamladık, ekibimizi genişlettik',
    'company.history.2020': '2020 - Dijital Dönüşüm',
    'company.history.2020.desc': 'Bulut çözümleri ve AI entegrasyonlarına odaklandık',
    'company.history.2025': '2025 - Liderlik',
    'company.history.2025.desc': 'Sektörde öncü konuma ulaştık',
    'company.values.title': 'Değerlerimiz',
    'company.values.innovation': 'İnovasyon',
    'company.values.innovation.desc': 'Sürekli yenilik ve gelişim odaklı çalışıyoruz',
    'company.values.quality': 'Kalite',
    'company.values.quality.desc': 'Her projede mükemmellik standartlarını uyguluyoruz',
    'company.values.trust': 'Güven',
    'company.values.trust.desc': 'Müşterilerimizle uzun vadeli güven ilişkileri kuruyoruz',
    'company.values.teamwork': 'Takım Çalışması',
    'company.values.teamwork.desc': 'Birlikte daha güçlüyüz, birlikte başarıyoruz',
    'company.certifications.title': 'Sertifikalar ve Ortaklıklar',
    'company.certifications.iso': 'ISO 9001:2015 Kalite Yönetim Sistemi',
    'company.certifications.iso27001': 'ISO 27001 Bilgi Güvenliği',
    'company.certifications.microsoft': 'Microsoft Gold Partner',
    'company.certifications.aws': 'AWS Advanced Consulting Partner',
    
    // Mission & Vision Page
    'mission.title': 'Misyon ve Vizyon',
    'mission.subtitle': 'Geleceğe yönelik hedeflerimiz',
    'mission.our.title': 'Misyonumuz',
    'mission.our.desc': 'İşletmelere özel, yenilikçi ve sürdürülebilir teknoloji çözümleri sunarak, dijital dönüşüm süreçlerinde güvenilir bir iş ortağı olmak. Müşterilerimizin başarısını kendi başarımız olarak görerek, en yüksek kalite standartlarında hizmet vermek.',
    'vision.our.title': 'Vizyonumuz',
    'vision.our.desc': 'Türkiye\'nin ve bölgenin önde gelen IT danışmanlık ve yazılım geliştirme şirketi olmak. Teknoloji alanındaki yenilikleri takip ederek, sektöre öncülük eden çözümler geliştirmek ve global pazarda tanınan bir marka haline gelmek.',
    'mission.goals.title': 'Stratejik Hedeflerimiz',
    'mission.goals.1': 'Müşteri Memnuniyeti',
    'mission.goals.1.desc': 'Her projede %100 müşteri memnuniyeti sağlamak',
    'mission.goals.2': 'Teknoloji Liderliği',
    'mission.goals.2.desc': 'En güncel teknolojileri projelerimize entegre etmek',
    'mission.goals.3': 'Sürdürülebilir Büyüme',
    'mission.goals.3.desc': 'Yıllık %30 büyüme hedefi ile ilerlemek',
    'mission.goals.4': 'Global Genişleme',
    'mission.goals.4.desc': 'Uluslararası pazarlarda varlık göstermek',
    'mission.approach.title': 'Çalışma Yaklaşımımız',
    'mission.approach.desc': 'Agile metodolojiler, DevOps kültürü ve sürekli iyileştirme prensipleriyle çalışıyoruz. Her projede müşteri odaklı, şeffaf ve işbirlikçi bir yaklaşım benimsiyoruz.',
    
    // Career Page
    'career.title': 'Kariyer',
    'career.subtitle': 'Ekibimize katılın',
    'career.intro.title': 'Teknolobi\'de Çalışmak',
    'career.intro.desc': 'Teknolobi\'de, yetenekli ve tutkulu profesyonellerle birlikte çalışma fırsatı bulacaksınız. Sürekli öğrenme, gelişim ve inovasyon odaklı çalışma ortamımızda kariyerinizi ilerletebilirsiniz.',
    'career.culture.title': 'Şirket Kültürümüz',
    'career.culture.learning': 'Sürekli Öğrenme',
    'career.culture.learning.desc': 'Eğitim ve gelişim programları',
    'career.culture.balance': 'İş-Yaşam Dengesi',
    'career.culture.balance.desc': 'Esnek çalışma saatleri ve uzaktan çalışma',
    'career.culture.innovation': 'İnovasyon',
    'career.culture.innovation.desc': 'Yeni fikirlere açık ortam',
    'career.culture.team': 'Takım Ruhu',
    'career.culture.team.desc': 'İşbirlikçi ve destekleyici ekip',
    'career.benefits.title': 'Çalışan Avantajları',
    'career.benefits.competitive': 'Rekabetçi Maaş',
    'career.benefits.health': 'Özel Sağlık Sigortası',
    'career.benefits.training': 'Eğitim ve Sertifikasyon Desteği',
    'career.benefits.remote': 'Uzaktan Çalışma İmkanı',
    'career.benefits.social': 'Sosyal Aktiviteler',
    'career.benefits.career': 'Kariyer Gelişim Programları',
    'career.positions.title': 'Açık Pozisyonlar',
    'career.positions.desc': 'Ekibimize katılmak için açık pozisyonlarımıza göz atın',
    'career.cta': 'Başvuru Yap',
    'career.apply.title': 'Başvuru Yapın',
    'career.apply.desc': 'CV\'nizi ve motivasyon mektubunuzu kariyer@teknolobi.com adresine gönderin',
    
    // Software Development Service Page
    'service.software.title': 'Yazılım Geliştirme',
    'service.software.subtitle': 'İşletmenize özel, ölçeklenebilir ve güvenli yazılım çözümleri',
    'service.software.hero.desc': 'Modern teknolojiler ve en iyi uygulamalarla, işletmenizin ihtiyaçlarına tam uygun yazılım çözümleri geliştiriyoruz. Web, mobil ve kurumsal uygulamalardan mikroservis mimarilerine kadar geniş yelpazede hizmet sunuyoruz.',
    'service.software.technologies.title': 'Kullandığımız Teknolojiler',
    'service.software.tech.web': 'Web Uygulamaları',
    'service.software.tech.web.desc': 'React, Vue.js, Angular ile modern web uygulamaları',
    'service.software.tech.mobile': 'Mobil Uygulamalar',
    'service.software.tech.mobile.desc': 'React Native, Flutter ile cross-platform mobil çözümler',
    'service.software.tech.backend': 'Backend Sistemler',
    'service.software.tech.backend.desc': 'Node.js, Python, Java ile güçlü backend altyapıları',
    'service.software.tech.database': 'Veritabanı Yönetimi',
    'service.software.tech.database.desc': 'PostgreSQL, MongoDB, Redis ile veri yönetimi',
    'service.software.process.title': 'Geliştirme Sürecimiz',
    'service.software.process.1': 'Analiz ve Planlama',
    'service.software.process.1.desc': 'İhtiyaç analizi ve proje planlaması',
    'service.software.process.2': 'Tasarım',
    'service.software.process.2.desc': 'UX/UI tasarım ve mimari tasarım',
    'service.software.process.3': 'Geliştirme',
    'service.software.process.3.desc': 'Agile metodoloji ile yazılım geliştirme',
    'service.software.process.4': 'Test',
    'service.software.process.4.desc': 'Kapsamlı test ve kalite güvence',
    'service.software.process.5': 'Yayınlama',
    'service.software.process.5.desc': 'Canlı ortama güvenli geçiş',
    'service.software.process.6': 'Destek',
    'service.software.process.6.desc': '7/24 teknik destek ve bakım',
    'service.software.benefits.title': 'Avantajlarımız',
    'service.software.benefit.1': 'Hızlı Geliştirme',
    'service.software.benefit.1.desc': '%50 daha hızlı proje teslimi',
    'service.software.benefit.2': 'Yüksek Performans',
    'service.software.benefit.2.desc': '3x daha iyi sistem performansı',
    'service.software.benefit.3': 'Güvenilirlik',
    'service.software.benefit.3.desc': '%99.9 uptime garantisi',
    'service.software.benefit.4': 'Sürekli Destek',
    'service.software.benefit.4.desc': '7/24 teknik destek hizmeti',
    
    // Digital Transformation Service Page
    'service.digital.title': 'Dijital Dönüşüm',
    'service.digital.subtitle': 'İş süreçlerinizi dijitalleştirerek verimliliği artırın',
    'service.digital.hero.desc': 'Dijital dönüşüm yolculuğunuzda yanınızdayız. Süreç otomasyonu, veri analitiği ve bulut migrasyonu ile işletmenizi geleceğe hazırlıyoruz.',
    'service.digital.areas.title': 'Dönüşüm Alanlarımız',
    'service.digital.area.automation': 'Süreç Otomasyonu',
    'service.digital.area.automation.desc': 'Manuel süreçleri otomatikleştirerek zaman ve maliyet tasarrufu',
    'service.digital.area.analytics': 'Veri Analitiği',
    'service.digital.area.analytics.desc': 'Verilerinizi anlamlı içgörülere dönüştürme',
    'service.digital.area.cloud': 'Bulut Migrasyonu',
    'service.digital.area.cloud.desc': 'Sistemlerinizi bulut altyapısına taşıma',
    'service.digital.area.integration': 'Sistem Entegrasyonu',
    'service.digital.area.integration.desc': 'Farklı sistemleri birbirine bağlama',
    'service.digital.benefits.title': 'Dönüşüm Faydaları',
    'service.digital.benefit.efficiency': 'Verimlilik Artışı',
    'service.digital.benefit.efficiency.value': '%40',
    'service.digital.benefit.cost': 'Maliyet Azaltma',
    'service.digital.benefit.cost.value': '%30',
    'service.digital.benefit.speed': 'Hız Kazanımı',
    'service.digital.benefit.speed.value': '2x',
    
    // IT Consulting Service Page
    'service.consulting.title': 'IT Danışmanlığı',
    'service.consulting.subtitle': 'Teknoloji stratejinizi oluşturun, doğru çözümlerle ilerleyin',
    'service.consulting.hero.desc': 'Deneyimli danışmanlarımızla teknoloji stratejinizi belirleyin, doğru yatırımları yapın ve rekabet avantajı kazanın.',
    'service.consulting.services.title': 'Danışmanlık Hizmetlerimiz',
    'service.consulting.service.strategy': 'Teknoloji Stratejisi',
    'service.consulting.service.strategy.desc': 'İş hedeflerinize uygun teknoloji stratejisi oluşturma',
    'service.consulting.service.architecture': 'Mimari Tasarım',
    'service.consulting.service.architecture.desc': 'Ölçeklenebilir ve güvenli sistem mimarisi tasarımı',
    'service.consulting.service.security': 'Güvenlik Danışmanlığı',
    'service.consulting.service.security.desc': 'Siber güvenlik risk analizi ve çözümleri',
    'service.consulting.service.optimization': 'Performans Optimizasyonu',
    'service.consulting.service.optimization.desc': 'Mevcut sistemlerin performans iyileştirmesi',
    'service.consulting.expertise.title': 'Uzmanlık Alanlarımız',
    'service.consulting.expertise.enterprise': 'Kurumsal Sistemler',
    'service.consulting.expertise.enterprise.years': '15+ yıl deneyim',
    'service.consulting.expertise.cloud': 'Bulut Teknolojileri',
    'service.consulting.expertise.cloud.years': '10+ yıl deneyim',
    'service.consulting.expertise.security': 'Siber Güvenlik',
    'service.consulting.expertise.security.years': '12+ yıl deneyim',
    
    // Cloud Solutions Service Page
    'service.cloud.title': 'Bulut Çözümleri',
    'service.cloud.subtitle': 'Modern bulut altyapısı ile işletmenizi her yerden yönetin',
    'service.cloud.hero.desc': 'AWS, Azure ve Google Cloud platformlarında uzman ekibimizle bulut altyapınızı kurun, yönetin ve optimize edin.',
    'service.cloud.platforms.title': 'Bulut Platformları',
    'service.cloud.platform.aws': 'AWS Partner',
    'service.cloud.platform.azure': 'Azure Certified',
    'service.cloud.platform.gcp': 'Google Cloud Partner',
    'service.cloud.services.title': 'Bulut Hizmetlerimiz',
    'service.cloud.service.migration': 'Bulut Migrasyonu',
    'service.cloud.service.migration.desc': 'Mevcut sistemlerinizi buluta güvenle taşıma',
    'service.cloud.service.infrastructure': 'Altyapı Yönetimi',
    'service.cloud.service.infrastructure.desc': 'Bulut altyapınızın kurulumu ve yönetimi',
    'service.cloud.service.devops': 'DevOps & CI/CD',
    'service.cloud.service.devops.desc': 'Otomatik deployment ve sürekli entegrasyon',
    'service.cloud.service.security': 'Bulut Güvenliği',
    'service.cloud.service.security.desc': 'Bulut ortamınızın güvenlik yönetimi',
    'service.cloud.benefits.title': 'Bulut Avantajları',
    'service.cloud.benefit.scalability': 'Ölçeklenebilirlik',
    'service.cloud.benefit.scalability.value': '10x',
    'service.cloud.benefit.availability': 'Erişilebilirlik',
    'service.cloud.benefit.availability.value': '%99.99',
    'service.cloud.benefit.cost': 'Maliyet Tasarrufu',
    'service.cloud.benefit.cost.value': '-%40',
    
    // Common Service Page Elements
    'service.cta.title': 'Projeniz İçin Bizimle İletişime Geçin',
    'service.cta.subtitle': 'Uzman ekibimiz size en uygun çözümü sunmak için hazır',
    'service.cta.button': 'Ücretsiz Danışmanlık Alın',
    
    // Contact Page
    'contact.title': 'Bize Ulaşın',
    'contact.subtitle': 'Projeleriniz için bizimle iletişime geçin',
    'contact.form.name': 'Ad Soyad',
    'contact.form.email': 'E-posta',
    'contact.form.phone': 'Telefon',
    'contact.form.company': 'Şirket',
    'contact.form.service': 'İlgilendiğiniz Hizmet',
    'contact.form.message': 'Mesajınız',
    'contact.form.submit': 'Gönder',
    'contact.info.title': 'İletişim Bilgileri',
    'contact.info.address': 'Adres',
    'contact.info.hours': 'Çalışma Saatleri',
    'contact.info.hours.value': 'Pazartesi - Cuma: 09:00 - 18:00',
    
    // Verilobi Product Page
    'verilobi.hero.title': 'Verilobi',
    'verilobi.hero.tagline': 'Şantiye Puantaj ve Maliyet Analiz Sistemi',
    'verilobi.hero.description': 'Yüz tanıma teknolojisi ile şantiyelerdeki personel puantajlarını otomatik oluşturun, maliyet analizi yapın ve iş gücünüzü verimli yönetin.',
    'verilobi.cta.demo': 'Demo Talep Et',
    'verilobi.cta.contact': 'Bize Ulaşın',
    
    // BeachBot Product Page
    'beachbot.hero.title': 'BeachBot',
    'beachbot.hero.tagline': 'Akıllı Plaj ve Beach Club Yönetim Sistemi',
    'beachbot.hero.description': 'Rezervasyondan siparişe, mutfak yönetiminden ödemeye kadar tüm süreçleri dijitalleştirin. Garson ihtiyacını azaltın, müşteri memnuniyetini artırın.',
    'beachbot.cta.demo': 'Demo Talep Et',
    'beachbot.cta.contact': 'Bize Ulaşın',
    
    // Common Product Page Elements
    'product.features.title': 'Özellikler',
    'product.benefits.title': 'Avantajlar',
    'product.usecases.title': 'Kullanım Alanları',
    'product.technology.title': 'Teknoloji',
    'product.screenshots.title': 'Ekran Görüntüleri',
    'product.cta.title': 'Hemen Başlayın',
    'product.cta.subtitle': 'Ürünümüzü denemek veya daha fazla bilgi almak için bizimle iletişime geçin',
  },
  en: {
    // SEO Meta
    'meta.title': 'Teknolobi - IT Consulting and Software Development',
    'meta.description': 'Transform your business for the future with Teknolobi. We provide custom software development, IT consulting, and cloud solutions.',
    'meta.keywords': 'IT consulting, software development, digital transformation, cloud solutions, custom software, Teknolobi',
    
    // Header
    'nav.about': 'About',
    'nav.company': 'Company Profile',
    'nav.missionvision': 'Mission & Vision',
    'nav.career': 'Career',
    'nav.services': 'Services',
    'nav.services.software': 'Software Development',
    'nav.services.digital': 'Digital Transformation',
    'nav.services.consulting': 'IT Consulting',
    'nav.services.cloud': 'Cloud Solutions',
    'nav.products': 'Products',
    'nav.projects': 'Projects',
    'nav.blog': 'Blog',
    'nav.contact': 'Contact',
    'nav.contactButton': 'Contact Us',
    
    // Hero
    'hero.title': 'Technology Leader, Solution Partner',
    'hero.subtitle': 'We transform your business for the future with digital transformation. We are with you with customized software solutions and IT consulting.',
    'hero.cta.primary': 'Explore Our Services',
    'hero.cta.secondary': 'Contact Us',
    
    // Services
    'services.title': 'Our Services',
    'services.subtitle': 'We offer technology solutions tailored to your business needs',
    'services.custom.title': 'Custom Software Development',
    'services.custom.desc': 'We develop scalable and secure software solutions tailored to your business.',
    'services.digital.title': 'Digital Transformation',
    'services.digital.desc': 'We digitize your business processes to increase efficiency and reduce costs.',
    'services.consulting.title': 'IT Consulting',
    'services.consulting.desc': 'We create your technology strategy and guide you with the right solutions.',
    'services.cloud.title': 'Cloud Solutions',
    'services.cloud.desc': 'Manage your business from anywhere with modern cloud infrastructure.',
    
    // Products
    'products.title': 'Our Products',
    'products.subtitle': 'Leading the industry with our innovative technology products',
    'products.beachbot.title': 'BeachBot',
    'products.beachbot.desc': 'Smart robot technology that automates beach management. Offers cleaning, security, and customer experience together.',
    'products.verilobi.title': 'Verilobi',
    'products.verilobi.desc': 'Business intelligence and data analytics platform. Transform your data into meaningful insights and make informed decisions.',
    'products.cta': 'Learn More',
    
    // Why Us
    'why.title': 'Why Teknolobi?',
    'why.subtitle': 'Values that make us different',
    'why.experience.title': 'Years of Experience',
    'why.experience.value': '15+',
    'why.projects.title': 'Completed Projects',
    'why.projects.value': '200+',
    'why.clients.title': 'Happy Clients',
    'why.clients.value': '150+',
    'why.team.title': 'Expert Team',
    'why.team.value': '50+',
    
    // Testimonials
    'testimonials.title': 'What Our Clients Say?',
    'testimonials.subtitle': 'Our success stories',
    
    // CTA
    'cta.title': "Let's Bring Your Project to Life",
    'cta.subtitle': 'Meet our expert team and determine the most suitable solution for your business together.',
    'cta.button': 'Request Free Consultation',
    
    // Footer
    'footer.about.title': 'Teknolobi',
    'footer.slogan': 'Value-Adding Solutions',
    'footer.about.desc': 'Leading company in IT consulting and software development.',
    'footer.links.title': 'Quick Links',
    'footer.contact.title': 'Contact',
    'footer.office.turkey': 'Turkey Office',
    'footer.office.canada': 'Canada Office',
    'footer.address.turkey': '23 Nisan Mh. 249. Sok. NOS Plaza 15/1 Nilufer Bursa',
    'footer.address.canada': '6 Chartwell Rd. Unit 416 M8Z 0H2 Toronto Canada',
    'footer.phone.turkey': '+90 850 333 30 40',
    'footer.phone.canada': '+1 647 984 77 42',
    'footer.email': 'info@teknolobi.com',
    'footer.rights': '© 2025 Teknolobi. All rights reserved.',
    
    // Company Profile Page
    'company.title': 'Company Profile',
    'company.subtitle': 'About Teknolobi',
    'company.intro.title': 'Shaping the Future with the Power of Technology',
    'company.intro.desc': 'Since 2010, Teknolobi has been operating as a leading company in IT consulting and software development. We aim to be a reliable business partner in the digital transformation journey of businesses by providing the most innovative technology solutions to our customers.',
    'company.history.title': 'Our History',
    'company.history.2010': '2010 - Foundation',
    'company.history.2010.desc': 'Started with a small team in Istanbul',
    'company.history.2015': '2015 - Growth',
    'company.history.2015.desc': 'Completed 50+ projects, expanded our team',
    'company.history.2020': '2020 - Digital Transformation',
    'company.history.2020.desc': 'Focused on cloud solutions and AI integrations',
    'company.history.2025': '2025 - Leadership',
    'company.history.2025.desc': 'Reached a leading position in the industry',
    'company.values.title': 'Our Values',
    'company.values.innovation': 'Innovation',
    'company.values.innovation.desc': 'We work with a focus on continuous innovation and development',
    'company.values.quality': 'Quality',
    'company.values.quality.desc': 'We apply excellence standards in every project',
    'company.values.trust': 'Trust',
    'company.values.trust.desc': 'We build long-term trust relationships with our customers',
    'company.values.teamwork': 'Teamwork',
    'company.values.teamwork.desc': 'Together we are stronger, together we succeed',
    'company.certifications.title': 'Certifications and Partnerships',
    'company.certifications.iso': 'ISO 9001:2015 Quality Management System',
    'company.certifications.iso27001': 'ISO 27001 Information Security',
    'company.certifications.microsoft': 'Microsoft Gold Partner',
    'company.certifications.aws': 'AWS Advanced Consulting Partner',
    
    // Mission & Vision Page
    'mission.title': 'Mission & Vision',
    'mission.subtitle': 'Our goals for the future',
    'mission.our.title': 'Our Mission',
    'mission.our.desc': 'To be a reliable business partner in digital transformation processes by providing customized, innovative and sustainable technology solutions to businesses. To provide service at the highest quality standards by considering the success of our customers as our own success.',
    'vision.our.title': 'Our Vision',
    'vision.our.desc': 'To become the leading IT consulting and software development company in Turkey and the region. To develop solutions that lead the industry by following innovations in the field of technology and to become a globally recognized brand.',
    'mission.goals.title': 'Our Strategic Goals',
    'mission.goals.1': 'Customer Satisfaction',
    'mission.goals.1.desc': 'Ensuring 100% customer satisfaction in every project',
    'mission.goals.2': 'Technology Leadership',
    'mission.goals.2.desc': 'Integrating the latest technologies into our projects',
    'mission.goals.3': 'Sustainable Growth',
    'mission.goals.3.desc': 'Moving forward with an annual growth target of 30%',
    'mission.goals.4': 'Global Expansion',
    'mission.goals.4.desc': 'Establishing presence in international markets',
    'mission.approach.title': 'Our Working Approach',
    'mission.approach.desc': 'We work with Agile methodologies, DevOps culture and continuous improvement principles. We adopt a customer-oriented, transparent and collaborative approach in every project.',
    
    // Career Page
    'career.title': 'Career',
    'career.subtitle': 'Join our team',
    'career.intro.title': 'Working at Teknolobi',
    'career.intro.desc': 'At Teknolobi, you will have the opportunity to work with talented and passionate professionals. You can advance your career in our continuous learning, development and innovation-focused work environment.',
    'career.culture.title': 'Our Company Culture',
    'career.culture.learning': 'Continuous Learning',
    'career.culture.learning.desc': 'Training and development programs',
    'career.culture.balance': 'Work-Life Balance',
    'career.culture.balance.desc': 'Flexible working hours and remote work',
    'career.culture.innovation': 'Innovation',
    'career.culture.innovation.desc': 'Environment open to new ideas',
    'career.culture.team': 'Team Spirit',
    'career.culture.team.desc': 'Collaborative and supportive team',
    'career.benefits.title': 'Employee Benefits',
    'career.benefits.competitive': 'Competitive Salary',
    'career.benefits.health': 'Private Health Insurance',
    'career.benefits.training': 'Training and Certification Support',
    'career.benefits.remote': 'Remote Work Opportunity',
    'career.benefits.social': 'Social Activities',
    'career.benefits.career': 'Career Development Programs',
    'career.positions.title': 'Open Positions',
    'career.positions.desc': 'Check out our open positions to join our team',
    'career.cta': 'Apply Now',
    'career.apply.title': 'Apply',
    'career.apply.desc': 'Send your CV and motivation letter to kariyer@teknolobi.com',
    
    // Software Development Service Page
    'service.software.title': 'Software Development',
    'service.software.subtitle': 'Custom, scalable and secure software solutions for your business',
    'service.software.hero.desc': 'We develop software solutions perfectly suited to your business needs using modern technologies and best practices. We offer a wide range of services from web, mobile and enterprise applications to microservice architectures.',
    'service.software.technologies.title': 'Technologies We Use',
    'service.software.tech.web': 'Web Applications',
    'service.software.tech.web.desc': 'Modern web apps with React, Vue.js, Angular',
    'service.software.tech.mobile': 'Mobile Applications',
    'service.software.tech.mobile.desc': 'Cross-platform mobile solutions with React Native, Flutter',
    'service.software.tech.backend': 'Backend Systems',
    'service.software.tech.backend.desc': 'Powerful backend infrastructure with Node.js, Python, Java',
    'service.software.tech.database': 'Database Management',
    'service.software.tech.database.desc': 'Data management with PostgreSQL, MongoDB, Redis',
    'service.software.process.title': 'Our Development Process',
    'service.software.process.1': 'Analysis & Planning',
    'service.software.process.1.desc': 'Requirements analysis and project planning',
    'service.software.process.2': 'Design',
    'service.software.process.2.desc': 'UX/UI design and architecture design',
    'service.software.process.3': 'Development',
    'service.software.process.3.desc': 'Software development with Agile methodology',
    'service.software.process.4': 'Testing',
    'service.software.process.4.desc': 'Comprehensive testing and quality assurance',
    'service.software.process.5': 'Deployment',
    'service.software.process.5.desc': 'Safe transition to production',
    'service.software.process.6': 'Support',
    'service.software.process.6.desc': '24/7 technical support and maintenance',
    'service.software.benefits.title': 'Our Advantages',
    'service.software.benefit.1': 'Fast Development',
    'service.software.benefit.1.desc': '50% faster project delivery',
    'service.software.benefit.2': 'High Performance',
    'service.software.benefit.2.desc': '3x better system performance',
    'service.software.benefit.3': 'Reliability',
    'service.software.benefit.3.desc': '99.9% uptime guarantee',
    'service.software.benefit.4': 'Continuous Support',
    'service.software.benefit.4.desc': '24/7 technical support service',
    
    // Digital Transformation Service Page
    'service.digital.title': 'Digital Transformation',
    'service.digital.subtitle': 'Increase efficiency by digitizing your business processes',
    'service.digital.hero.desc': 'We are with you on your digital transformation journey. We prepare your business for the future with process automation, data analytics and cloud migration.',
    'service.digital.areas.title': 'Our Transformation Areas',
    'service.digital.area.automation': 'Process Automation',
    'service.digital.area.automation.desc': 'Save time and cost by automating manual processes',
    'service.digital.area.analytics': 'Data Analytics',
    'service.digital.area.analytics.desc': 'Transform your data into meaningful insights',
    'service.digital.area.cloud': 'Cloud Migration',
    'service.digital.area.cloud.desc': 'Move your systems to cloud infrastructure',
    'service.digital.area.integration': 'System Integration',
    'service.digital.area.integration.desc': 'Connect different systems together',
    'service.digital.benefits.title': 'Transformation Benefits',
    'service.digital.benefit.efficiency': 'Efficiency Increase',
    'service.digital.benefit.efficiency.value': '40%',
    'service.digital.benefit.cost': 'Cost Reduction',
    'service.digital.benefit.cost.value': '30%',
    'service.digital.benefit.speed': 'Speed Gain',
    'service.digital.benefit.speed.value': '2x',
    
    // IT Consulting Service Page
    'service.consulting.title': 'IT Consulting',
    'service.consulting.subtitle': 'Create your technology strategy, move forward with the right solutions',
    'service.consulting.hero.desc': 'Define your technology strategy with our experienced consultants, make the right investments and gain competitive advantage.',
    'service.consulting.services.title': 'Our Consulting Services',
    'service.consulting.service.strategy': 'Technology Strategy',
    'service.consulting.service.strategy.desc': 'Creating technology strategy aligned with business goals',
    'service.consulting.service.architecture': 'Architecture Design',
    'service.consulting.service.architecture.desc': 'Scalable and secure system architecture design',
    'service.consulting.service.security': 'Security Consulting',
    'service.consulting.service.security.desc': 'Cybersecurity risk analysis and solutions',
    'service.consulting.service.optimization': 'Performance Optimization',
    'service.consulting.service.optimization.desc': 'Performance improvement of existing systems',
    'service.consulting.expertise.title': 'Our Areas of Expertise',
    'service.consulting.expertise.enterprise': 'Enterprise Systems',
    'service.consulting.expertise.enterprise.years': '15+ years experience',
    'service.consulting.expertise.cloud': 'Cloud Technologies',
    'service.consulting.expertise.cloud.years': '10+ years experience',
    'service.consulting.expertise.security': 'Cybersecurity',
    'service.consulting.expertise.security.years': '12+ years experience',
    
    // Cloud Solutions Service Page
    'service.cloud.title': 'Cloud Solutions',
    'service.cloud.subtitle': 'Manage your business from anywhere with modern cloud infrastructure',
    'service.cloud.hero.desc': 'Build, manage and optimize your cloud infrastructure with our expert team on AWS, Azure and Google Cloud platforms.',
    'service.cloud.platforms.title': 'Cloud Platforms',
    'service.cloud.platform.aws': 'AWS Partner',
    'service.cloud.platform.azure': 'Azure Certified',
    'service.cloud.platform.gcp': 'Google Cloud Partner',
    'service.cloud.services.title': 'Our Cloud Services',
    'service.cloud.service.migration': 'Cloud Migration',
    'service.cloud.service.migration.desc': 'Safely migrate your existing systems to cloud',
    'service.cloud.service.infrastructure': 'Infrastructure Management',
    'service.cloud.service.infrastructure.desc': 'Setup and management of your cloud infrastructure',
    'service.cloud.service.devops': 'DevOps & CI/CD',
    'service.cloud.service.devops.desc': 'Automatic deployment and continuous integration',
    'service.cloud.service.security': 'Cloud Security',
    'service.cloud.service.security.desc': 'Security management of your cloud environment',
    'service.cloud.benefits.title': 'Cloud Advantages',
    'service.cloud.benefit.scalability': 'Scalability',
    'service.cloud.benefit.scalability.value': '10x',
    'service.cloud.benefit.availability': 'Availability',
    'service.cloud.benefit.availability.value': '99.99%',
    'service.cloud.benefit.cost': 'Cost Savings',
    'service.cloud.benefit.cost.value': '-40%',
    
    // Common Service Page Elements
    'service.cta.title': 'Contact Us For Your Project',
    'service.cta.subtitle': 'Our expert team is ready to offer you the most suitable solution',
    'service.cta.button': 'Get Free Consultation',
    
    // Contact Page
    'contact.title': 'Contact Us',
    'contact.subtitle': 'Get in touch with us for your projects',
    'contact.form.name': 'Full Name',
    'contact.form.email': 'Email',
    'contact.form.phone': 'Phone',
    'contact.form.company': 'Company',
    'contact.form.service': 'Service of Interest',
    'contact.form.message': 'Your Message',
    'contact.form.submit': 'Submit',
    'contact.info.title': 'Contact Information',
    'contact.info.address': 'Address',
    'contact.info.hours': 'Working Hours',
    'contact.info.hours.value': 'Monday - Friday: 09:00 - 18:00',
    
    // Blog Page
    'blog.title': 'Blogumuz',
    'blog.titleEN': 'Our Blog',
    'blog.subtitle': 'Teknoloji dünyasından en güncel haberler ve içgörüler',
    'blog.subtitleEN': 'Latest news and insights from the technology world',
    'blog.readMore': 'Devamını Oku',
    'blog.readMoreEN': 'Read More',
    'blog.minRead': 'dakika okuma',
    'blog.minReadEN': 'min read',
    'blog.publishedDate': 'Yayın Tarihi',
    'blog.publishedDateEN': 'Published Date',
    'blog.category': 'Kategori',
    'blog.categoryEN': 'Category',
    'blog.tags': 'Etiketler',
    'blog.tagsEN': 'Tags',
    'blog.relatedPosts': 'İlgili Yazılar',
    'blog.relatedPostsEN': 'Related Posts',
    'blog.allPosts': 'Tüm Yazılar',
    'blog.allPostsEN': 'All Posts',
    'blog.filterByCategory': 'Kategoriye Göre Filtrele',
    'blog.filterByCategoryEN': 'Filter by Category',
    'blog.allCategories': 'Tüm Kategoriler',
    'blog.allCategoriesEN': 'All Categories',
    
    // Verilobi Product Page
    'verilobi.hero.title': 'Verilobi',
    'verilobi.hero.tagline': 'Construction Site Attendance and Cost Analysis System',
    'verilobi.hero.description': 'Automatically create personnel attendance records at construction sites with facial recognition technology, perform cost analysis, and manage your workforce efficiently.',
    'verilobi.cta.demo': 'Request Demo',
    'verilobi.cta.contact': 'Contact Us',
    
    // BeachBot Product Page
    'beachbot.hero.title': 'BeachBot',
    'beachbot.hero.tagline': 'Smart Beach and Beach Club Management System',
    'beachbot.hero.description': 'Digitize all processes from reservation to ordering, kitchen management to payment. Reduce waiter dependency, increase customer satisfaction.',
    'beachbot.cta.demo': 'Request Demo',
    'beachbot.cta.contact': 'Contact Us',
    
    // Common Product Page Elements
    'product.features.title': 'Features',
    'product.benefits.title': 'Benefits',
    'product.usecases.title': 'Use Cases',
    'product.technology.title': 'Technology',
    'product.screenshots.title': 'Screenshots',
    'product.cta.title': 'Get Started Now',
    'product.cta.subtitle': 'Contact us to try our product or get more information',
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>('tr');

  const t = (key: string): string => {
    const translationObj = translations[language] as Record<string, string>;
    return translationObj[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
}